import { useState, useEffect, useRef } from "react";
import { ReactComponent as AddTarget } from "../../../Assets/Icons/addTarget.svg";
import { ReactComponent as EditTarget } from "../../../Assets/Icons/editTarget.svg";
import TargetModal from "./TargetModal";
import {
  createTarget,
  getTarget,
  updateTarget,
} from "../../../services/fundraiserService";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../../utils/toast";

export default function Target() {
  const [targetAmount, setTargetAmount] = useState("");
  const [achievedAmount, setAchievedAmount] = useState(0);
  const [timeLeft, setTimeLeft] = useState("");
  const [targetState, setTargetState] = useState("no-target");
  const [previousTarget, setPreviousTarget] = useState(0);
  const [previousAchieved, setPreviousAchieved] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("set");
  const [activeTargetId, setActiveTargetId] = useState(null);
  const [hasActivePlaceholderTarget, setHasActivePlaceholderTarget] =
    useState(false);

  // Responsive circle size state
  const [circleSize, setCircleSize] = useState(300);
  const [strokeWidth, setStrokeWidth] = useState(20);
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const fundraiser = useSelector((state) => state?.authReducer?.tokenData);

  const getErrorMessage = (error) =>
    error?.message ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.payload?.message ||
    "Unable to save target. Please try again.";
  const isActiveTargetConflict = (error) =>
    getErrorMessage(error)
      .toLowerCase()
      .includes("latest target is still active or not cleared");
  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const parseDurationToDays = (durationValue) => {
    if (durationValue == null) return 0;
    const raw = String(durationValue).toLowerCase().trim();
    if (raw.includes("1_year") || raw.includes("1 year")) return 365;
    if (raw.includes("6_month") || raw.includes("6 month")) return 180;
    if (raw.includes("3_month") || raw.includes("3 month")) return 90;
    if (raw.includes("1_month") || raw.includes("1 month")) return 30;
    const numeric = parseInt(raw, 10);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  // Get target data from API
  const fetchTargetData = async () => {
    try {
      const targetPromise = await dispatch(getTarget())?.unwrap();
      if (targetPromise?.success !== true || !targetPromise?.data) {
        setTargetState("no-target");
        return;
      }

      const data = targetPromise.data;
      const targets = Array.isArray(data?.targets) ? data.targets : [];
      const latest =
        targets.find((t) => t?.status === true) || targets[0] || data;

      const targetAmountValue = toNumber(
        latest?.targetAmount ?? data?.targetAmount,
      );
      const achievedAmountValue = toNumber(
        latest?.achievedAmount ?? data?.achievedAmount,
      );
      const lastTargetAmountValue = toNumber(
        latest?.lastTargetAmount ??
          data?.lastTargetAmount ??
          targets.find((t) => t?.status !== true)?.targetAmount,
      );
      const lastAchievedAmountValue = toNumber(
        latest?.lastAchievedAmount ??
          data?.lastAchievedAmount ??
          targets.find((t) => t?.status !== true)?.achievedAmount,
      );
      const durationValue = latest?.duration ?? data?.duration;
      const createdAtValue = latest?.createdAt ?? data?.createdAt;
      const latestStatus =
        typeof latest?.status === "boolean" ? latest.status : data?.status;
      const resolvedTargetId =
        latest?._id || latest?.id || data?._id || data?.id;

      setTargetAmount(targetAmountValue);
      setAchievedAmount(achievedAmountValue);
      setPreviousTarget(lastTargetAmountValue);
      setPreviousAchieved(lastAchievedAmountValue);
      setActiveTargetId(resolvedTargetId || null);
      setHasActivePlaceholderTarget(
        latestStatus === true && targetAmountValue <= 0,
      );

      const timeLeftData = calculateTimeLeft(durationValue, createdAtValue);
      setTimeLeft(timeLeftData.formattedTime);

      if (targetAmountValue <= 0) {
        setTargetState(lastTargetAmountValue > 0 ? "ended" : "no-target");
      } else {
        setTargetState(achievedAmountValue > 0 ? "in-progress" : "initial");
      }
    } catch (error) {
      setTargetState("no-target");
    }
  };

  // Calculate time left based on duration and created date
  const calculateTimeLeft = (durationDays, createdAt) => {
    if (!createdAt || !durationDays) {
      return { formattedTime: "", expired: true };
    }

    const endDate = new Date(createdAt);
    // Parse duration as an integer to ensure proper calculation
    const durationInt = parseDurationToDays(durationDays);
    endDate.setDate(endDate.getDate() + durationInt);

    const now = new Date();
    const timeDiff = endDate - now;

    // If expired
    if (timeDiff <= 0) {
      return { formattedTime: "", expired: true };
    }

    // Calculate months, days, hours, minutes, seconds
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    const months = Math.floor(days / 30);
    const remainingDays = days % 30;

    // Format the time left
    let formattedTime = "";

    if (months > 0 || days > 7) {
      // Show in months and days format
      formattedTime =
        months > 0
          ? `${months} Month${months > 1 ? "s" : ""} ${remainingDays} Day${
              remainingDays !== 1 ? "s" : ""
            }`
          : `${days} Day${days !== 1 ? "s" : ""}`;
    } else if (days > 0) {
      // Show in days format
      formattedTime = `${days} Day${days !== 1 ? "s" : ""} ${hours} Hr${
        hours !== 1 ? "s" : ""
      }`;
    } else {
      // Show in hours, minutes, seconds format
      formattedTime = `${hours}hrs : ${minutes}m : ${seconds}s`;
    }

    return { formattedTime, expired: false };
  };

  useEffect(() => {
    fetchTargetData();
  }, []);

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1536) {
        setCircleSize(240);
        setStrokeWidth(18);
      } else if (window.innerWidth >= 1280) {
        // xl breakpoint
        setCircleSize(220);
        setStrokeWidth(16);
      } else if (window.innerWidth >= 1024) {
        // lg breakpoint
        setCircleSize(200);
        setStrokeWidth(14);
      } else if (window.innerWidth >= 900) {
        // md breakpoint
        setCircleSize(240);
        setStrokeWidth(18);
      } else if (window.innerWidth >= 768) {
        // md breakpoint
        setCircleSize(200);
        setStrokeWidth(14);
      } else {
        // sm and below
        setCircleSize(165);
        setStrokeWidth(12);
      }
    };

    // Initial call
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOpenModal = (mode) => {
    if (mode === "set") {
      setTargetAmount("");
    }
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const handleTargetSubmit = async ({ amount, duration }) => {
    const normalizedAmount = Number(amount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      toast.error("Please enter an amount greater than 0.");
      return false;
    }

    // Convert duration string to number of days
    let daysCount;
    switch (duration) {
      case "1 Month":
        daysCount = "1_month";
        break;
      case "3 Months":
        daysCount = "3_month";
        break;
      case "6 Months":
        daysCount = "6_month";
        break;
      case "1 Year":
        daysCount = "1_year";
        break;
      default:
        daysCount = "1_month"; // Default to 30 days if unknown duration
    }

    try {
      if (modalMode === "update") {
        const targetPromise = await dispatch(
          updateTarget({
            id: activeTargetId || fundraiser?.user?._id,
            targetAmount: normalizedAmount,
            duration: daysCount,
          }),
        )?.unwrap();

        if (targetPromise?.success === true) {
          await fetchTargetData();
          return true;
        }
      } else {
        // Backend can return an "active" placeholder target with amount 0.
        // In that case we must update existing target instead of creating.
        if (hasActivePlaceholderTarget) {
          const updatePromise = await dispatch(
            updateTarget({
              id: activeTargetId || fundraiser?.user?._id,
              targetAmount: normalizedAmount,
              duration: daysCount,
            }),
          )?.unwrap();

          if (updatePromise?.success === true) {
            await fetchTargetData();
            return true;
          }
          return false;
        }

        try {
          const targetPromise = await dispatch(
            createTarget({
              targetAmount: normalizedAmount,
              duration: daysCount,
            }),
          )?.unwrap();

          if (targetPromise?.success === true) {
            await fetchTargetData();
            return true;
          }
        } catch (createError) {
          // Backend may reject "create" while still allowing update of current target.
          if (!isActiveTargetConflict(createError)) {
            throw createError;
          }

          const updatePromise = await dispatch(
            updateTarget({
              id: activeTargetId || fundraiser?.user?._id,
              targetAmount: normalizedAmount,
              duration: daysCount,
            }),
          )?.unwrap();

          if (updatePromise?.success === true) {
            await fetchTargetData();
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    }
  };

  // Calculate progress percentage
  const progressPercentage = targetAmount
    ? Math.min(Math.round((achievedAmount / targetAmount) * 100), 100)
    : 0;

  const formatAmount = (amount) => {
    const num = Number(amount);
    return !isNaN(num) ? `$${num.toFixed(2)}` : "$0.00";
  };

  // target shout have toFixed(2) to show 2 decimal places
  const formattedTarget = formatAmount(targetAmount);
  const formattedAchieved = formatAmount(achievedAmount);
  // const formattedTarget = `$${targetAmount}` || "$0.00";
  // const formattedAchieved = `$${achievedAmount}` || "$0.00";

  // Format previous target values
  const formattedPreviousTarget = formatAmount(previousTarget);
  const formattedPreviousAchieved = formatAmount(previousAchieved);

  // Calculate circle properties
  const radius = (circleSize - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress =
    targetState === "no-target" || targetState === "ended"
      ? 0
      : progressPercentage;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const renderCenterContent = () => {
    if (targetState === "no-target") {
      return (
        <div className="text-center flex flex-col justify-center items-center gap-2">
          <p className="text-[14px] md:text-[16px] font-[700] text-[#000000] font-poppins">
            Add Target
          </p>
          <button
            onClick={() => handleOpenModal("set")}
            className="w-8 h-8 flex items-center justify-center mx-auto text-[#00A3E0] hover:text-blue-700 transition-colors cursor-pointer bg-transparent border-0 relative z-20"
          >
            <AddTarget className="text-2xl" />
          </button>
        </div>
      );
    } else if (targetState === "ended") {
      return (
        <div className="text-center flex flex-col justify-center items-center gap-2">
          <p className="text-[14px] md:text-[16px] font-[700] text-[#000000] font-poppins">
            Add Target
          </p>
          <button
            onClick={() => handleOpenModal("set")}
            className="w-8 h-8 flex items-center justify-center mx-auto text-[#00A3E0] hover:text-blue-700 transition-colors cursor-pointer bg-transparent border-0 relative z-20"
          >
            <AddTarget className="text-2xl" />
          </button>
        </div>
      );
    } else {
      return (
        <div className="text-center flex flex-col justify-center items-center gap-2">
          <p className="text-[14px] md:text-[16px] font-[700] text-[#000000] font-poppins">
            Target
          </p>
          <div className="flex items-center justify-center">
            <span className="text-[18px] md:text-[20px] font-[700] text-[#000000] font-poppins">
              {formattedTarget}
            </span>
            <button
              onClick={() => handleOpenModal("update")}
              className="ml-1 w-6 h-6 flex items-center justify-center text-blue-500 hover:text-blue-700 transition-colors cursor-pointer bg-transparent border-0 relative z-20"
            >
              <EditTarget />
            </button>
          </div>
        </div>
      );
    }
  };

  const renderRightContent = () => {
    if (targetState === "no-target") {
      return (
        <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end gap-2 font-poppins">
          <span className="text-[12px] font-[500] text-[#8C8C8C]">
            Current Raised
          </span>
          <span className="text-[20px] font-[700] text-[#8c8c8c]">$0.00</span>
        </div>
      );
    } else if (targetState === "ended") {
      return (
        <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end gap-3 font-poppins">
          <div className="flex flex-col items-start md:items-end">
            <span className="text-[12px] font-[500] text-[#8C8C8C]">
              Previous Target
            </span>
            <span className="text-[18px] font-[700] text-[#1C2434]">
              {formattedPreviousTarget}
            </span>
          </div>
          <div className="flex flex-col items-start md:items-end">
            <span className="text-[12px] font-[500] text-[#8C8C8C]">
              Achieved
            </span>
            <span className="text-[18px] font-[700] text-[#8c8c8c]">
              {formattedPreviousAchieved ? formattedPreviousAchieved : "$0.00"}
            </span>
          </div>
        </div>
      );
    } else {
      const remainingAmount = targetAmount
        ? Math.max(0, targetAmount - achievedAmount)
        : 0;
      const formattedRemaining = formatAmount(remainingAmount);

      const milestones = [];
      if (progressPercentage >= 100) {
        milestones.push(
          "Congratulations! - You've reached 100% of your fundraising goal.",
        );
      } else if (progressPercentage >= 75) {
        milestones.push(
          "Congratulations! - You've reached 75% of your fundraising goal.",
        );
      } else if (progressPercentage >= 50) {
        milestones.push(
          "Congratulations! - You've reached 50% of your fundraising goal.",
        );
      } else if (progressPercentage >= 25) {
        milestones.push(
          "Congratulations! - You've reached 25% of your fundraising goal.",
        );
      }

      return (
        <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end gap-3 font-poppins min-w-[200px]">
          <div className="flex flex-col items-start md:items-end leading-tight">
            <span className="text-[11px] font-[500] text-[#8C8C8C]">
              Target Amount
            </span>
            <span className="text-[18px] font-[700] text-[#1c2434]">
              {formattedTarget}
            </span>
          </div>

          <div className="flex flex-col items-start md:items-end leading-tight">
            <span className="text-[11px] font-[500] text-[#8C8C8C]">
              Current Raised
            </span>
            <span className="text-[18px] font-[700] text-[#10B981]">
              {formattedAchieved}
            </span>
          </div>

          <div className="flex flex-col items-start md:items-end leading-tight">
            <span className="text-[11px] font-[500] text-[#8C8C8C]">
              Percentage Complete
            </span>
            <span className="text-[16px] font-[700] text-[#00A3E0]">
              {progressPercentage}%
            </span>
          </div>

          <div className="flex flex-col items-start md:items-end leading-tight">
            <span className="text-[11px] font-[500] text-[#8C8C8C]">
              Remaining Balance
            </span>
            <span className="text-[14px] font-[600] text-[#EF4444]">
              {formattedRemaining}
            </span>
          </div>

          {milestones.length > 0 && (
            <div className="mt-2 bg-[#f0f9ff] border border-[#bae6fd] text-[#0369a1] text-[10px] sm:text-[11px] font-[500] px-3 py-2 rounded-[6px] text-left md:text-right max-w-[220px]">
              {milestones[0]}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <>
      <div className="w-full h-full summary bg-white p-4" ref={containerRef}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <h2 className="text-[16px] md:text-[18px] lg:text[20px] font-[600] text-[#303030] font-poppins">
              Fundraising Target
            </h2>
            <p className="text-[12px] md:text-[14px] lg:text[16px] font-[400] text-[#8C8C8C] font-poppins">
              This target changes over time.
            </p>
          </div>
          {timeLeft &&
            targetState !== "ended" &&
            targetState !== "no-target" && (
              <div className="text-right">
                <p className="text-[12px] md:text-[14px] font-[700] text-[#000000] font-poppins">
                  Time Left:{" "}
                  <span className="text-[#8C8C8C] font-[400]">{timeLeft}</span>
                </p>
              </div>
            )}
        </div>

        <div className="flex flex-col md:flex-row justify-center items-center gap-3 md:gap-4 lg:gap-5 mt-4">
          <div className="relative flex items-center justify-center">
            <div className="absolute z-20">{renderCenterContent()}</div>

            <svg
              className="transform -rotate-90 pointer-events-none"
              width={circleSize}
              height={circleSize}
            >
              <circle
                className="text-gray-200"
                strokeWidth={strokeWidth}
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx={circleSize / 2}
                cy={circleSize / 2}
              />
              <circle
                className="text-[#00A3E0]"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx={circleSize / 2}
                cy={circleSize / 2}
              />
            </svg>
          </div>

          <div className="pl-1 md:pl-2">{renderRightContent()}</div>
        </div>
      </div>

      <TargetModal
        target={targetAmount}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleTargetSubmit}
        mode={modalMode}
      />
    </>
  );
}
